<template>
  <section
    data-test="home-discover-mcp"
    :aria-label="t('home_cockpit.discover.mcp.title')"
    class="mcp-discover rounded-theme-md border border-theme-border bg-theme-surface px-4 py-3
      dark:border-theme-border dark:bg-theme-surface"
  >
    <div class="flex min-w-0 items-center gap-3">
      <span
        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-sm
          bg-theme-brand/10 text-theme-brand dark:bg-theme-brand/10 dark:text-theme-brand"
        aria-hidden="true"
      >
        <icon :path="mdiStarFourPoints" size="16" />
      </span>
      <div class="min-w-0 flex-1 basis-64">
        <p class="text-[10px] uppercase tracking-wide text-theme-brand dark:text-theme-brand">
          {{ t('home_cockpit.discover.eyebrow') }} · MCP
        </p>
        <h2 class="text-sm font-medium text-theme-text dark:text-theme-text">
          {{ t('home_cockpit.discover.mcp.title') }}
        </h2>
        <p class="text-xs text-theme-text-secondary dark:text-theme-text-secondary">
          {{ t('home_cockpit.discover.mcp.body') }}
        </p>
      </div>
    </div>
    <div class="mcp-discover-actions flex flex-wrap items-center justify-end gap-3">
      <app-button
        v-for="provider in providers"
        :key="provider.id"
        :class="`mcp-provider--${provider.id}`"
        :aria-label="t('home_cockpit.discover.mcp.open_provider', { provider: t(`home_cockpit.discover.mcp.${provider.id}.name`) })"
        :title="t(`home_cockpit.discover.mcp.${provider.id}.hint`)"
        aria-haspopup="dialog"
        variant="outline"
        tone="neutral"
        size="md"
        @click="selectProvider(provider)"
      >
        <template v-if="provider.id === 'claude'">
          <img src="/images/brands/claude-wordmark-white.svg" alt="" aria-hidden="true" class="h-5 w-auto" />
          <span class="sr-only">{{ t('home_cockpit.discover.mcp.claude.name') }}</span>
        </template>
        <template v-else>
          <icon :path="provider.icon" size="20" w="w-5" h="h-5" />
          <span>{{ t(`home_cockpit.discover.mcp.${provider.id}.name`) }}</span>
        </template>
      </app-button>
    </div>
    <app-modal
      :model-value="Boolean(selectedProvider)"
      :title="selectedProvider ? t('home_cockpit.discover.mcp.setup_title', { provider: t(`home_cockpit.discover.mcp.${selectedProvider.id}.name`) }) : ''"
      :close-label="t('home_cockpit.discover.mcp.close')"
      body-class="space-y-4"
      @update:model-value="closeDialog"
    >
      <template v-if="selectedProvider">
        <p class="text-sm text-theme-text-secondary dark:text-theme-text-secondary">
          {{ t(`home_cockpit.discover.mcp.${selectedProvider.id}.hint`) }}
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <code class="min-w-0 select-all break-all text-xs text-theme-text dark:text-theme-text">{{ mcpUrl }}</code>
          <app-button
            :label="t('home_cockpit.discover.mcp.copy_url')"
            :icon="mdiContentCopy"
            variant="ghost"
            size="sm"
            @click="copyUrl"
          />
          <span role="status" class="text-xs text-theme-text-secondary dark:text-theme-text-secondary">
            {{ copyStatus ? t(`home_cockpit.discover.mcp.${copyStatus}`) : '' }}
          </span>
        </div>
        <ol class="list-decimal space-y-2 pl-5 text-sm text-theme-text-secondary dark:text-theme-text-secondary">
          <li v-for="step in 3" :key="step">
            {{ t(`home_cockpit.discover.mcp.${selectedProvider.id}.step_${step}`) }}
          </li>
        </ol>
      </template>
      <template v-if="selectedProvider" #footer>
        <app-button
          :href="selectedProvider.docs"
          :label="t('home_cockpit.discover.mcp.instructions')"
          :icon-right="mdiOpenInNew"
          target="_blank"
          rel="noopener noreferrer"
          variant="ghost"
          size="sm"
        />
        <app-button
          :href="selectedProvider.href"
          :class="`mcp-provider--${selectedProvider.id}`"
          :label="t('home_cockpit.discover.mcp.open_now', { provider: t(`home_cockpit.discover.mcp.${selectedProvider.id}.name`) })"
          :icon="selectedProvider.icon"
          :icon-right="mdiOpenInNew"
          target="_blank"
          rel="noopener noreferrer"
          variant="outline"
          tone="neutral"
          size="sm"
        />
      </template>
    </app-modal>
  </section>
</template>

<script setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { mdiContentCopy, mdiOpenInNew, mdiStarFourPoints } from '@mdi/js';
  import Icon from '@/components/Icon';
  import AppButton from '@/components/ui/buttons/AppButton.vue';
  import AppModal from '@/components/ui/overlays/AppModal.vue';
  import { getAppEnvironment } from '@/app/runtime';
  import { getMcpConnectionOptions } from '@/config/domain/mcpConnections';

  const { t } = useI18n();
  const { mcpUrl, providers } = getMcpConnectionOptions(getAppEnvironment());
  const selectedProvider = ref(null);
  const copyStatus = ref('');
  function closeDialog() {
    selectedProvider.value = null;
  }

  function selectProvider(provider) {
    selectedProvider.value = provider;
    copyUrl();
  }

  async function copyUrl() {
    copyStatus.value = '';
    try {
      await navigator.clipboard.writeText(mcpUrl);
      copyStatus.value = 'copied';
    } catch {
      copyStatus.value = 'copy_failed';
    }
  }
</script>

<style scoped>
  @font-face {
    font-family: 'OpenAI Sans';
    src: url('../../../styles/fonts/openai-sans-regular.woff2') format('woff2');
    font-style: normal;
    font-weight: 400;
    font-display: swap;
  }

  .mcp-discover {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 1rem;
  }

  .mcp-discover > :first-child {
    flex: 1 1 16rem;
  }

  .mcp-discover-actions {
    margin-left: auto;
    align-self: center;
  }

  .app-button-theme.mcp-provider--claude,
  .app-button-theme.mcp-provider--claude:hover:not(:disabled) {
    background: rgb(var(--color-brand-claude));
    border-color: rgb(var(--color-brand-claude));
    color: rgb(var(--color-static-white));
  }

  .app-button-theme.mcp-provider--chatgpt,
  .app-button-theme.mcp-provider--chatgpt:hover:not(:disabled) {
    background: rgb(var(--color-static-white));
    border-color: rgb(var(--color-border-strong));
    color: rgb(var(--color-static-black));
    font-family: 'OpenAI Sans', Arial, sans-serif;
    font-weight: 400;
  }
</style>
